import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useDebounce } from "use-debounce";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export const SearchBox = memo(function SearchBox({
  value,
  onChange,
  placeholder = "Buscar...",
}: SearchBoxProps) {
  const [localValue, setLocalValue] = useState(value);
  // Rastreia o último valor vindo de fora (prop) ou empurrado para fora (onChange),
  // para distinguir "reset externo" da nossa própria propagação voltando.
  const externalValueRef = useRef(value);
  const [debouncedValue] = useDebounce(localValue, 300);

  // Sincroniza quando o pai reseta o valor (ex.: botão "limpar tudo").
  useEffect(() => {
    if (value !== externalValueRef.current) {
      externalValueRef.current = value;
      setLocalValue(value);
    }
  }, [value]);

  // Propaga o valor com debounce apenas quando difere do que o pai já tem.
  useEffect(() => {
    if (debouncedValue !== externalValueRef.current) {
      externalValueRef.current = debouncedValue;
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange]);

  const handleClear = useCallback(() => {
    externalValueRef.current = "";
    setLocalValue("");
    onChange("");
  }, [onChange]);

  return (
    <div className="group relative w-full max-w-md flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
      <Input
        placeholder={placeholder}
        className={cn(
          "h-10 w-full bg-transparent pl-9 pr-9 text-foreground transition-all placeholder:text-muted-foreground",
          "border-input focus:border-primary focus:ring-1 focus:ring-primary/20",
        )}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
      />
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
          aria-label="Limpar busca"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
});
